// src/pages/dashboard/PayrollAllowances.js
// FIXED: Working days now properly extracted from calculation_metadata

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  DatePicker,
  Table,
  Space,
  InputNumber,
  message,
  Typography,
  Card,
  Checkbox,
  Select,
  Tooltip
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  DollarOutlined,
  CalendarOutlined,
  PercentageOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { supabase } from '../../services/supabase';
import dayjs from 'dayjs';
import { StatusPill, formatRs } from '../../components/ui';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(isBetween);

const { Text } = Typography;
const { Option } = Select;

const PayrollAllowances = ({ visible, employee, onClose, companyId }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [allowances, setAllowances] = useState([]);
  const [allowanceTypes, setAllowanceTypes] = useState([]);
  const [selectedAllowances, setSelectedAllowances] = useState([]);
  const [workingDays, setWorkingDays] = useState(0);
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible && employee && companyId) {
      fetchAllowanceTypes();
      fetchEmployeeAllowances();
      calculateWorkingDays();
      setSelectedAllowances([]);
    }
  }, [visible, employee, companyId]);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setSelectedAllowances([]);
      setAllowances([]);
      setAllowanceTypes([]);
      setWorkingDays(0);
      setLoading(false);
      setSubmitting(false);
    }
  }, [visible, form]);

  // FIXED: Calculate working days from calculation_metadata at root level
  const calculateWorkingDays = () => {
    try {
      console.log('📊 Employee data structure:', employee);

      // Try multiple possible locations for calculation_metadata
      let metadata = null;

      // First, try root level (most likely location)
      if (employee?.calculation_metadata) {
        metadata = typeof employee.calculation_metadata === 'string'
          ? JSON.parse(employee.calculation_metadata)
          : employee.calculation_metadata;
      }
      // Fallback: try nested in employees
      else if (employee?.employees?.calculation_metadata) {
        metadata = typeof employee.employees.calculation_metadata === 'string'
          ? JSON.parse(employee.employees.calculation_metadata)
          : employee.employees.calculation_metadata;
      }

      if (metadata) {
        const days = metadata.working_days || 0;
        setWorkingDays(days);
        console.log('✅ Working days extracted:', days, 'from metadata:', metadata);
      } else {
        console.warn('⚠️ No calculation_metadata found in:', {
          root: !!employee?.calculation_metadata,
          nested: !!employee?.employees?.calculation_metadata,
          employeeKeys: Object.keys(employee || {})
        });
        setWorkingDays(0);
      }
    } catch (error) {
      console.error('❌ Error parsing calculation_metadata:', error);
      setWorkingDays(0);
    }
  };

  const fetchAllowanceTypes = async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('allowance_types')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;

      console.log('📋 Fetched allowance types:', data);
      setAllowanceTypes(data || []);
    } catch (error) {
      console.error('Error fetching allowance types:', error);
      message.error('Failed to fetch allowance types');
      setAllowanceTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeAllowances = async () => {
    if (!employee?.employeeId || !companyId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('employee_allowances')
        .select(`
          id,
          amount,
          start_date,
          end_date,
          is_recurring,
          allowance_type_id,
          allowance_types (
            id,
            name,
            is_percentage,
            is_taxable,
            calculation_method
          )
        `)
        .eq('employee_id', employee.employeeId)
        .eq('company_id', companyId)
        .order('start_date', { ascending: false });

      if (error) throw error;

      console.log('💰 Fetched employee allowances:', data);

      // DEBUG: Log each allowance's calculation method
      if (data && data.length > 0) {
        data.forEach(allowance => {
          console.log(`Allowance: ${allowance.allowance_types?.name}, Method: ${allowance.allowance_types?.calculation_method}, Amount: ${allowance.amount}`);
        });
      }

      setAllowances(data || []);
    } catch (error) {
      console.error('Error fetching employee allowances:', error);
      message.error('Failed to fetch employee allowances');
      setAllowances([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAllowance = async () => {
    if (!companyId || !employee?.employeeId) {
      message.error('Missing required information');
      return;
    }

    try {
      const values = await form.validateFields();
      setSubmitting(true);

      // Handle optional date range
      let startDate = null;
      let endDate = null;

      if (values.date_range && values.date_range.length === 2) {
        startDate = values.date_range[0].format('YYYY-MM-DD');
        endDate = values.date_range[1].format('YYYY-MM-DD');
      } else {
        // If no date range provided, set to a very long period (e.g., 10 years)
        startDate = dayjs().format('YYYY-MM-DD');
        endDate = dayjs().add(10, 'years').format('YYYY-MM-DD');
      }

      const allowanceData = {
        company_id: companyId,
        employee_id: employee.employeeId,
        allowance_type_id: values.allowance_type_id,
        amount: values.amount,
        start_date: startDate,
        end_date: endDate,
        is_recurring: values.is_recurring || false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('employee_allowances')
        .insert([allowanceData])
        .select(`
          id,
          amount,
          start_date,
          end_date,
          is_recurring,
          allowance_type_id,
          allowance_types (
            id,
            name,
            is_percentage,
            is_taxable,
            calculation_method
          )
        `)
        .single();

      if (error) throw error;

      console.log('✅ Allowance added:', data);
      message.success('Allowance added successfully');
      form.resetFields();
      await fetchEmployeeAllowances();

    } catch (validationError) {
      if (validationError.errorFields) {
        message.error('Please fill in all required fields');
      } else {
        console.error('Error adding allowance:', validationError);
        message.error('Failed to add allowance');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveAllowance = async (allowanceId) => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from('employee_allowances')
        .delete()
        .eq('id', allowanceId);

      if (error) throw error;

      message.success('Allowance removed successfully');
      await fetchEmployeeAllowances();
    } catch (error) {
      console.error('Error removing allowance:', error);
      message.error('Failed to remove allowance');
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Calculate allowance with working days
  const calculateAllowanceAmount = (allowance) => {
    const calculationMethod = allowance.allowance_types?.calculation_method || 'fixed';
    const baseSalary = employee?.gross_pay || 0;

    console.log(`🧮 Calculating ${allowance.allowance_types?.name}:`, {
      method: calculationMethod,
      amount: allowance.amount,
      baseSalary,
      workingDays
    });

    let result = 0;

    switch (calculationMethod) {
      case 'percentage':
        result = baseSalary * (allowance.amount / 100);
        break;
      case 'per_day':
        result = allowance.amount * workingDays;
        console.log(`  ➡️ Per-day calculation: ${allowance.amount} × ${workingDays} days = ${result}`);
        break;
      case 'fixed':
      default:
        result = allowance.amount;
        break;
    }

    return result;
  };

  const validateDateRange = (_, value) => {
    if (!value || value.length !== 2) {
      return Promise.resolve();
    }
    if (value[0].isAfter(value[1])) {
      return Promise.reject(new Error('Start date must be before end date'));
    }
    return Promise.resolve();
  };

  const handleCancel = () => {
    if (submitting) {
      message.warning('Please wait for the current operation to complete');
      return;
    }
    onClose();
  };

  const handleSave = async () => {
    if (selectedAllowances.length === 0) {
      message.info('No allowances selected. Closing without changes.');
      onClose();
      return;
    }

    try {
      setLoading(true);

      // Calculate total allowances based on selected items
      const activeAllowances = allowances.filter(a => selectedAllowances.includes(a.id));
      const totalAllowances = activeAllowances.reduce((sum, allowance) => {
        return sum + calculateAllowanceAmount(allowance);
      }, 0);

      // Update payroll record with new allowances total
      const { error } = await supabase
        .from('payroll_records')
        .update({
          total_allowances: totalAllowances,
          gross_pay: (employee?.gross_pay || 0) - (employee?.total_allowances || 0) + totalAllowances,
          net_pay: (employee?.gross_pay || 0) - (employee?.total_allowances || 0) + totalAllowances - (employee?.total_deductions || 0),
          allowance_details: JSON.stringify(activeAllowances.map(a => ({
            id: a.id,
            name: a.allowance_types?.name,
            amount: calculateAllowanceAmount(a),
            calculation_method: a.allowance_types?.calculation_method,
            original_amount: a.amount,
            working_days: a.allowance_types?.calculation_method === 'per_day' ? workingDays : null
          }))),
          updated_at: new Date().toISOString()
        })
        .eq('id', employee.payrollRecordId)
        .eq('company_id', companyId);

      if (error) throw error;

      message.success(`Allowances updated. Total: Rs${totalAllowances.toFixed(2)}`);
      onClose();
    } catch (error) {
      console.error('Error saving allowances:', error);
      message.error('Failed to save allowances');
    } finally {
      setLoading(false);
    }
  };

  const getCalculationMethodIcon = (method) => {
    switch (method) {
      case 'percentage':
        return <PercentageOutlined style={{ color: 'var(--theme-primary-ink)' }} />;
      case 'per_day':
        return <CalendarOutlined style={{ color: 'var(--theme-positive-ink)' }} />;
      case 'fixed':
      default:
        return <DollarOutlined style={{ color: 'var(--theme-muted)' }} />;
    }
  };

  const getCalculationMethodTag = (method) => {
    const tones = {
      percentage: 'primary',
      per_day: 'positive',
      fixed: 'neutral'
    };
    const labels = {
      percentage: 'Percentage',
      per_day: 'Per Day',
      fixed: 'Fixed'
    };
    return (
      <StatusPill tone={tones[method] || 'neutral'}>
        {labels[method] || method}
      </StatusPill>
    );
  };

  // Calculate totals
  const baseSalary = (employee?.gross_pay || 0) - (employee?.total_allowances || 0);
  const activeAllowances = allowances.filter(a => selectedAllowances.includes(a.id));
  const totalAllowances = activeAllowances.reduce((sum, allowance) => {
    return sum + calculateAllowanceAmount(allowance);
  }, 0);
  const newGrossPay = baseSalary + totalAllowances;
  const netPay = newGrossPay - (employee?.total_deductions || 0);

  const columns = [
    {
      title: '',
      width: 50,
      render: (_, record) => (
        <Checkbox
          checked={selectedAllowances.includes(record.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedAllowances([...selectedAllowances, record.id]);
            } else {
              setSelectedAllowances(selectedAllowances.filter(id => id !== record.id));
            }
          }}
        />
      )
    },
    {
      title: 'Allowance Type',
      dataIndex: ['allowance_types', 'name'],
      key: 'name',
      render: (name, record) => (
        <Space direction="vertical" size={4}>
          <Space>
            {getCalculationMethodIcon(record.allowance_types?.calculation_method)}
            <Text strong>{name}</Text>
          </Space>
          {getCalculationMethodTag(record.allowance_types?.calculation_method)}
        </Space>
      )
    },
    {
      title: 'Calculation',
      key: 'calculation',
      width: 200,
      render: (_, record) => {
        const method = record.allowance_types?.calculation_method || 'fixed';
        const amount = record.amount;

        if (method === 'percentage') {
          return (
            <Space direction="vertical" size={0}>
              <Text>{amount}% of base salary</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Rs{baseSalary.toFixed(2)} × {amount}%
              </Text>
            </Space>
          );
        } else if (method === 'per_day') {
          return (
            <Space direction="vertical" size={0}>
              <Text>Rs{amount} per day</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Rs{amount} × {workingDays} days
              </Text>
            </Space>
          );
        } else {
          return <Text>Rs{amount.toFixed(2)} (Fixed)</Text>;
        }
      }
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (_, record) => {
        const amount = calculateAllowanceAmount(record);
        return (
          <span className="num num--positive">
            {formatRs(amount, 2)}
          </span>
        );
      }
    },
    {
      title: 'Period',
      key: 'period',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: '12px' }}>
            {dayjs(record.start_date).format('DD/MM/YYYY')}
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            to {dayjs(record.end_date).format('DD/MM/YYYY')}
          </Text>
        </Space>
      )
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (_, record) => {
        const isTaxable = record.allowance_types?.is_taxable;
        const isRecurring = record.is_recurring;
        return (
          <Space direction="vertical" size={4} align="center">
            <StatusPill tone={isTaxable ? 'warning' : 'neutral'}>
              {isTaxable ? 'Taxable' : 'Non-taxable'}
            </StatusPill>
            {isRecurring && (
              <StatusPill tone="primary">Recurring</StatusPill>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveAllowance(record.id)}
          disabled={loading || submitting}
        />
      )
    }
  ];

  const tableHeader = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Space>
        <h3 className="section-title" style={{ fontSize: 15 }}>Employee allowances</h3>
        <span className="chip">{allowances.length} total</span>
      </Space>
      <Space>
        <Checkbox
          checked={selectedAllowances.length === activeAllowances.length && activeAllowances.length > 0}
          indeterminate={selectedAllowances.length > 0 && selectedAllowances.length < activeAllowances.length}
          onChange={(e) => {
            if (e.target.checked) {
              const activeAllowanceIds = activeAllowances.map(a => a.id);
              setSelectedAllowances(activeAllowanceIds);
            } else {
              setSelectedAllowances([]);
            }
          }}
          disabled={activeAllowances.length === 0}
        >
          Select All Active Allowances ({activeAllowances.length})
        </Checkbox>
      </Space>
    </div>
  );

  return (
    <Modal
      title={
        <Space>
          <DollarOutlined />
          {`Allowances - ${employee?.employees?.first_name} ${employee?.employees?.last_name}`}
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      width={1000}
      maskClosable={false}
      keyboard={false}
      footer={[
        <Button
          key="cancel"
          onClick={handleCancel}
          disabled={submitting}
        >
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={loading}
          onClick={handleSave}
          disabled={submitting}
        >
          Save Allowances
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <div className="soft-panel kv-list">
          <div>
            <span className="kv-list__label">Department</span>
            <span className="kv-list__value">{employee?.employees?.department || 'N/A'}</span>
          </div>
          <div>
            <span className="kv-list__label">Position</span>
            <span className="kv-list__value">{employee?.employees?.position || 'N/A'}</span>
          </div>
          <div>
            <span className="kv-list__label">
              Working days this period{' '}
              <Tooltip title="Used for per-day allowance calculations.">
                <InfoCircleOutlined />
              </Tooltip>
            </span>
            <span className="kv-list__value">{workingDays} days</span>
          </div>
          <div>
            <span className="kv-list__label">Base gross pay</span>
            <span className="kv-list__value">{formatRs(baseSalary, 2)}</span>
          </div>
          <div>
            <span className="kv-list__label">Current deductions</span>
            <span className="kv-list__value">{formatRs(employee?.total_deductions || 0, 2)}</span>
          </div>
        </div>

        <Card title="Add New Allowance" size="small">
          <Form
            form={form}
            layout="vertical"
            style={{ marginBottom: 0 }}
          >
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'end' }}>
              <Form.Item
                name="allowance_type_id"
                label="Allowance Type"
                rules={[{ required: true, message: 'Please select allowance type' }]}
                style={{ minWidth: '250px', flex: 1 }}
              >
                <Select
                  placeholder="Select Allowance Type"
                  loading={loading}
                  disabled={submitting}
                  showSearch
                  optionFilterProp="children"
                >
                  {allowanceTypes.map(type => {
                    const icon = getCalculationMethodIcon(type.calculation_method);
                    const method = type.calculation_method === 'percentage' ? '(%)' :
                                  type.calculation_method === 'per_day' ? '(/day)' :
                                  '(Fixed)';
                    return (
                      <Option key={type.id} value={type.id}>
                        {icon} {type.name} {method}
                        {type.is_taxable ? ' - Taxable' : ' - Non-Taxable'}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>

              <Form.Item
                name="amount"
                label="Amount"
                rules={[
                  { required: true, message: 'Please enter amount' },
                  { type: 'number', min: 0.01, message: 'Amount must be greater than 0' }
                ]}
                style={{ minWidth: '120px' }}
              >
                <InputNumber
                  placeholder="Amount"
                  min={0}
                  step={0.01}
                  style={{ width: '100%' }}
                  formatter={value => `Rs ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/Rs\s?|(,*)/g, '')}
                  disabled={submitting}
                />
              </Form.Item>

              <Form.Item
                name="date_range"
                label={
                  <Space>
                    Active Period
                    <Tooltip title="Optional: Leave empty for permanent allowances. Use date range for temporary allowances (e.g., project bonuses).">
                      <InfoCircleOutlined style={{ color: 'var(--theme-muted)' }} />
                    </Tooltip>
                  </Space>
                }
                rules={[{ validator: validateDateRange }]}
                style={{ minWidth: '250px' }}
              >
                <DatePicker.RangePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  disabled={submitting}
                  placeholder={['Start (Optional)', 'End (Optional)']}
                />
              </Form.Item>

              <Form.Item
                name="is_recurring"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Checkbox disabled={submitting}>Recurring</Checkbox>
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddAllowance}
                  loading={submitting}
                  disabled={loading}
                >
                  Add Allowance
                </Button>
              </Form.Item>
            </div>
          </Form>
        </Card>

        <Table
          columns={columns}
          dataSource={allowances}
          rowKey="id"
          pagination={false}
          loading={loading && !submitting}
          title={() => tableHeader}
          summary={() => allowances.length > 0 ? (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}></Table.Summary.Cell>
                <Table.Summary.Cell index={1} colSpan={2}>
                  Selected allowances total
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <span className="num num--positive">
                    {formatRs(totalAllowances, 2)}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} colSpan={3}></Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          ) : null}
        />

        <Card size="small">
          <div className="totals">
            <div className="totals__row">
              <span>Base gross pay</span>
              <span className="num">{formatRs(baseSalary, 2)}</span>
            </div>
            <div className="totals__row">
              <span>Selected allowances</span>
              <span className="num num--positive">+{formatRs(totalAllowances, 2)}</span>
            </div>
            <div className="totals__row">
              <span>New gross pay</span>
              <span className="num num--strong">{formatRs(newGrossPay, 2)}</span>
            </div>
            <div className="totals__row">
              <span>Deductions</span>
              <span className="num num--negative">-{formatRs(employee?.total_deductions || 0, 2)}</span>
            </div>
            <div className="totals__row totals__row--grand">
              <span>Net pay</span>
              <span className="num">{formatRs(netPay, 2)}</span>
            </div>
          </div>
        </Card>
      </Space>
    </Modal>
  );
};

export default PayrollAllowances;